/**
 * AgencyFeaturesPage — Plan × feature comparison matrix.
 *
 * Rows  = Agency-tier features (multi_client, sub_admins, white_label,
 *         smartlink_manual, campaign_grouping).
 * Cols  = Starter / Agency / Enterprise.
 * Cells = check (enabled) or dash (disabled).
 *
 * The caller's current plan column is highlighted. When the current plan
 * is below Agency (i.e. starter or unknown), an "Upgrade to Agency" CTA
 * is rendered; when already on Agency, an Enterprise upsell CTA appears.
 */
import { useAgencyFeaturesList } from "./hooks";
import {
	AGENCY_FEATURE_KEYS,
	AGENCY_FEATURE_META,
	PLAN_IDS,
	PLAN_FEATURE_MATRIX,
	PLAN_LABELS,
	toPlanId,
	type AgencyFeatureKey,
	type PlanId,
} from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const BTN_PRIMARY =
	"inline-flex items-center rounded-[6px] border border-sky-600 bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700";
const BTN_SECONDARY =
	"inline-flex items-center rounded-[6px] border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50";

function CheckIcon() {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 20 20"
			className="h-5 w-5 text-emerald-600"
			fill="currentColor"
		>
			<path
				fillRule="evenodd"
				d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 111.42-1.42l2.79 2.79 6.79-6.79a1 1 0 011.42 0z"
				clipRule="evenodd"
			/>
		</svg>
	);
}

function DashIcon() {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 20 20"
			className="h-5 w-5 text-zinc-300"
			fill="currentColor"
		>
			<path
				fillRule="evenodd"
				d="M4 10a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1z"
				clipRule="evenodd"
			/>
		</svg>
	);
}

interface UpgradeCtaProps {
	currentPlan: PlanId | null;
}

function UpgradeCta({ currentPlan }: UpgradeCtaProps) {
	// Already on top tier — no upsell.
	if (currentPlan === "enterprise") {
		return (
			<div
				className={CARD}
				data-testid="agency-features-cta-enterprise"
				aria-label="Plan status"
			>
				<h3 className="text-base font-semibold text-zinc-900">
					You're on Enterprise
				</h3>
				<p className="mt-1 text-sm text-zinc-600">
					Every Agency-tier capability is already included in your plan.
				</p>
			</div>
		);
	}

	// Mid-tier — push toward Enterprise (custom contracts, not a self-serve
	// upgrade flow).
	if (currentPlan === "agency") {
		return (
			<div
				className={CARD}
				data-testid="agency-features-cta-agency"
				aria-label="Enterprise upsell"
			>
				<h3 className="text-base font-semibold text-zinc-900">
					Need more? Talk to Enterprise.
				</h3>
				<p className="mt-1 text-sm text-zinc-600">
					Dedicated support, custom SLAs, and volume pricing.
				</p>
				<a
					href="/billing/contact-sales"
					className={`${BTN_SECONDARY} mt-4`}
					data-testid="agency-features-contact-sales"
				>
					Contact sales
				</a>
			</div>
		);
	}

	// Starter or unknown plan — primary upgrade CTA.
	return (
		<div
			className={CARD}
			data-testid="agency-features-cta-upgrade"
			aria-label="Upgrade to Agency"
		>
			<h3 className="text-base font-semibold text-zinc-900">
				Unlock the full Agency toolkit
			</h3>
			<p className="mt-1 text-sm text-zinc-600">
				Manage clients, invite sub-admins, white-label portals, and more.
			</p>
			<a
				href="/billing/upgrade"
				className={`${BTN_PRIMARY} mt-4`}
				data-testid="agency-features-upgrade"
			>
				Upgrade to Agency
			</a>
		</div>
	);
}

export function AgencyFeaturesPage() {
	const listQuery = useAgencyFeaturesList();

	const currentPlan = toPlanId(listQuery.data?.plan ?? null);

	return (
		<main
			data-testid="agency-features-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Agency features
				</p>
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
					Plan capabilities
				</h1>
				<p className="text-sm text-zinc-600">
					Compare what's included on each plan. Your current plan is
					highlighted.
				</p>
			</header>

			<section className={CARD} aria-label="Plan comparison matrix">
				{listQuery.isLoading ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="agency-features-loading"
					>
						Loading plan matrix.
					</p>
				) : listQuery.error ? (
					<p
						className="text-sm text-red-600"
						role="alert"
						data-testid="agency-features-error"
					>
						{listQuery.error.message}
					</p>
				) : (
					<div className="overflow-x-auto">
						<table
							className="w-full border-collapse text-sm"
							data-testid="agency-features-matrix"
						>
							<thead>
								<tr className="text-left">
									<th
										scope="col"
										className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500"
									>
										Feature
									</th>
									{PLAN_IDS.map((plan) => {
										const isCurrent = plan === currentPlan;
										return (
											<th
												key={plan}
												scope="col"
												className={
													isCurrent
														? "rounded-t-[6px] bg-gradient-to-r from-emerald-50 to-sky-50 px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-sky-700"
														: "px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-zinc-500"
												}
												data-testid={`agency-features-col-${plan}`}
												data-current={isCurrent ? "true" : "false"}
											>
												<span className="block">{PLAN_LABELS[plan]}</span>
												{isCurrent ? (
													<span
														className="mt-1 inline-flex items-center rounded-full border border-sky-200 bg-white px-2 py-0.5 text-[10px] font-medium text-sky-700"
														data-testid="agency-features-current-plan-badge"
													>
														Current plan
													</span>
												) : null}
											</th>
										);
									})}
								</tr>
							</thead>
							<tbody>
								{AGENCY_FEATURE_KEYS.map((key: AgencyFeatureKey) => {
									const meta = AGENCY_FEATURE_META[key];
									return (
										<tr
											key={key}
											data-testid={`agency-features-row-${key}`}
										>
											<th
												scope="row"
												className="border-t border-zinc-200 px-4 py-3 text-left align-top"
											>
												<span className="block font-medium text-zinc-900">
													{meta.label}
												</span>
												<span className="mt-0.5 block text-xs text-zinc-500">
													{meta.description}
												</span>
											</th>
											{PLAN_IDS.map((plan) => {
												const enabled = PLAN_FEATURE_MATRIX[plan][key];
												const isCurrent = plan === currentPlan;
												return (
													<td
														key={plan}
														className={
															isCurrent
																? "border-t border-zinc-200 bg-gradient-to-r from-emerald-50/60 to-sky-50/60 px-4 py-3 text-center"
																: "border-t border-zinc-200 px-4 py-3 text-center"
														}
														data-testid={`agency-features-cell-${plan}-${key}`}
													>
														<span className="inline-flex items-center justify-center">
															{enabled ? <CheckIcon /> : <DashIcon />}
														</span>
														<span className="sr-only">
															{enabled ? "Included" : "Not included"}
														</span>
													</td>
												);
											})}
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</section>

			{listQuery.data && !listQuery.isLoading && !listQuery.error ? (
				<UpgradeCta currentPlan={currentPlan} />
			) : null}
		</main>
	);
}

export default AgencyFeaturesPage;
